import React, { Dispatch, ReactNode, SetStateAction, useRef } from 'react';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import classNames from 'classnames';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Routine } from '../../../../types/Fisiofit/routine';
import { Reservation } from '../../../../types/reservation';
import { EXTERNAL_USER_STATUSES } from '../../../../types/externalUser';
import { getUserAvatarProps } from '../../../../utils/avatarUtils';
import { UseResourcePanelValue } from '../../../../hooks/useResourcePanel';
import { CouponDisplay } from '../../../../components/CouponDisplay/CouponDisplay';
import { ExternalUserSignedDocuments } from '../../../../components/ExternalUserSignedDocuments/ExternalUserSignedDocuments';
import { UserProfileAppInvitation } from '../../../../components/UserProfileAppInvitation/UserProfileAppInvitation';
import { ExerciseProgress } from '../../../../components/ExerciseProgress/ExerciseProgress';
import { HasOngoingInjuryToggle } from '../../../../components/HasOngoingInjuryToggle/HasOngoingInjuryToggle';
import { DiagnosticField } from '../DiagnosticField';
import { ObjectiveField } from '../ObjectiveField';
import { useProfileClient } from '../hooks/useProfileClient';
import { useProfilePermissions } from '../hooks/useProfilePermissions';
import { ProfileOrganization } from '../types';
import { getGenderDisplay, getUserStatusDisplay } from '../utils';
import { useReservationTypes } from '../../../../hooks/AppData/useReservationTypes';

// One label and its value in a profile group: the label is small and muted so the value carries the weight.
const ProfileField = ({
  label,
  labelId,
  className,
  children,
}: {
  label: string;
  labelId?: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className={classNames('profile-field', className)}>
    <dt id={labelId}>{label}</dt>
    <dd>{children}</dd>
  </div>
);

export const ClientInfoCard = ({
  user,
  organization,
  externalUserId,
  permissions,
  mutateUser,
  openReservationPanel,
  openRoutinePanel,
  setShowEditModal,
  setShowBulkCancel,
  showToggleStatusDialog,
  isTogglingStatus,
}: {
  user: ReturnType<typeof useProfileClient>['user'];
  organization: ProfileOrganization;
  externalUserId: string | undefined;
  permissions: ReturnType<typeof useProfilePermissions>;
  mutateUser: ReturnType<typeof useProfileClient>['mutateUser'];
  openReservationPanel: UseResourcePanelValue<Reservation>['openPanel'];
  openRoutinePanel: UseResourcePanelValue<Routine>['openPanel'];
  setShowEditModal: Dispatch<SetStateAction<boolean>>;
  setShowBulkCancel: Dispatch<SetStateAction<boolean>>;
  showToggleStatusDialog: () => void;
  isTogglingStatus: boolean;
}) => {
  const {
    userHasEditFeature,
    userHasCreateFisioReservationsFeature,
    canBulkCancelReservations,
    userHasViewRoutinesFeature,
    userHasCreateRoutinesFeature,
  } = permissions;
  const { withVisitTypeSuffix } = useReservationTypes();

  const menu = useRef<Menu>(null);
  const isActive = user?.status === EXTERNAL_USER_STATUSES.ACTIVE;
  const formatDate = (date: string) => format(new Date(date), "d 'de' MMMM 'de' yyyy");
  const branchName = organization?.branches?.find((branch) => branch.id === user?.branchId)?.name;

  // Destructive actions live in the overflow menu, so the header shows one primary action and neutral secondary ones.
  const moreActions: MenuItem[] = [
    ...(canBulkCancelReservations
      ? [
          {
            label: 'Cancelar reservas',
            icon: 'pi pi-calendar-times',
            className: 'menu-item-danger',
            command: () => setShowBulkCancel(true),
          },
        ]
      : []),
    ...(userHasEditFeature && isActive
      ? [
          {
            label: 'Dar de baja',
            icon: 'pi pi-user-minus',
            className: 'menu-item-danger',
            disabled: isTogglingStatus,
            command: showToggleStatusDialog,
          },
        ]
      : []),
  ];

  const openRoutine = () => {
    const { name, surnames, email, id, avatarUrl } = user ?? {};
    const panelRoutine =
      user?.currentRoutine ??
      ({
        name: `Rutina de ${name} ${surnames}`,
        organizationId: organization?.id,
        externalUser: { name, surnames, email, id, avatarUrl },
      } as Routine);
    openRoutinePanel(false, panelRoutine);
  };

  return (
    <Card className='profile-header'>
      <div className='profile-hero'>
        <Avatar {...getUserAvatarProps(user)} shape='circle' size='xlarge' />
        <div className='profile-hero-text'>
          <div className='profile-info-name'>
            <h2>
              {user?.name} {user?.surnames}
            </h2>
            <Tag
              value={getUserStatusDisplay(user?.status ?? EXTERNAL_USER_STATUSES.INACTIVE)}
              severity={isActive ? 'success' : 'danger'}
            />
          </div>
          {(user?.age || user?.gender) && (
            <div className='profile-info-age-gender'>
              <span>{user?.age ? `${user.age} años` : ''}</span>
              {user?.age && user?.gender && <span className='separator'>•</span>}
              <span>{user?.gender ? getGenderDisplay(user.gender) : ''}</span>
            </div>
          )}
        </div>
        <div className='profile-actions'>
          {userHasCreateFisioReservationsFeature && (
            <Button label='Crear reserva' icon='pi pi-calendar-plus' onClick={() => openReservationPanel(false)} />
          )}
          {userHasViewRoutinesFeature && (user?.currentRoutine || userHasCreateRoutinesFeature) && (
            <>
              <Button
                outlined
                label={user?.currentRoutine ? 'Ver rutina' : 'Crear rutina'}
                icon='pi pi-list'
                onClick={openRoutine}
              />
              <ExerciseProgress user={user} />
            </>
          )}
          {userHasEditFeature && (
            <Button
              outlined
              label='Editar'
              icon='pi pi-pencil'
              className='profile-edit-button'
              onClick={() => setShowEditModal(true)}
            />
          )}
          {userHasEditFeature && !isActive && (
            <Button
              outlined
              label='Dar de alta'
              icon='pi pi-user-plus'
              onClick={showToggleStatusDialog}
              loading={isTogglingStatus}
            />
          )}
          {moreActions.length > 0 && (
            <>
              <Menu model={moreActions} popup ref={menu} id='profile-more-actions' />
              <Button
                text
                icon='pi pi-ellipsis-v'
                aria-label='Más acciones'
                aria-haspopup
                aria-controls='profile-more-actions'
                onClick={(event) => menu.current?.toggle(event)}
              />
            </>
          )}
        </div>
      </div>

      <div className='profile-sections'>
        <section className='profile-field-group' aria-labelledby='profile-group-contact'>
          <h3 id='profile-group-contact'>Contacto</h3>
          <dl className='profile-fields'>
            <ProfileField label='Email'>{user?.email || '-'}</ProfileField>
            <ProfileField label='Teléfono'>{user?.phone || '-'}</ProfileField>
            <ProfileField label='DNI'>{user?.nationalId || '-'}</ProfileField>
            <ProfileField label='Dirección'>{user?.address || '-'}</ProfileField>
            {user?.profession && <ProfileField label='Profesión'>{user.profession}</ProfileField>}
          </dl>
        </section>

        <section className='profile-field-group' aria-labelledby='profile-group-client'>
          <h3 id='profile-group-client'>Cliente</h3>
          <dl className='profile-fields'>
            <ProfileField label='Cliente desde'>{user?.createdAt ? formatDate(user.createdAt) : '-'}</ProfileField>
            {user?.deactivationDate ? (
              <ProfileField label='Fecha de baja'>{formatDate(user.deactivationDate)}</ProfileField>
            ) : (
              user?.reactivationDate && <ProfileField label='Fecha de alta'>{formatDate(user.reactivationDate)}</ProfileField>
            )}
            {user?.latestReservationDate && (
              <ProfileField label={withVisitTypeSuffix('Última visita')}>{formatDate(user.latestReservationDate)}</ProfileField>
            )}
            <ProfileField label='Sucursal'>{branchName || '-'}</ProfileField>
            <ProfileField label='Supervisor'>
              {user?.supervisor ? (
                <span className='supervisor-info'>
                  <Avatar {...getUserAvatarProps(user.supervisor)} shape='circle' size='normal' />
                  <span>{`${user.supervisor.name} ${user.supervisor.surnames}`}</span>
                </span>
              ) : (
                '-'
              )}
            </ProfileField>
            <ProfileField label='Bonos activos' className='profile-info-coupons'>
              {user?.userCoupons?.length ? (
                user.userCoupons.map((coupon) => <CouponDisplay coupon={coupon} key={coupon.id} />)
              ) : (
                <span className='profile-field-empty'>No hay bonos activos</span>
              )}
            </ProfileField>
          </dl>
        </section>

        <section className='profile-field-group' aria-labelledby='profile-group-health'>
          <h3 id='profile-group-health'>Salud y app</h3>
          <dl className='profile-fields'>
            {user && (
              <ProfileField label='Lesión en curso' labelId='profile-field-injury'>
                <HasOngoingInjuryToggle
                  hasOngoingInjury={user.hasOngoingInjury}
                  hasEditFeature={userHasEditFeature}
                  mutateUser={mutateUser}
                  user={user}
                  labelId='profile-field-injury'
                />
              </ProfileField>
            )}
            <ProfileField label='Invitado a app'>
              <UserProfileAppInvitation user={user} hasEditFeature={userHasEditFeature} />
            </ProfileField>
            {organization?.config?.showGymUserField && (
              <ProfileField label='Usuario de gym'>
                <i
                  className={classNames('pi', {
                    'pi-check-circle': user?.isGymUser,
                    'pi-times-circle': !user?.isGymUser,
                    success: user?.isGymUser,
                    error: !user?.isGymUser,
                  })}
                ></i>{' '}
                {user?.isGymUser ? 'Sí' : 'No'}
              </ProfileField>
            )}
          </dl>
        </section>
      </div>

      {(organization?.config?.hasUserDiagnosticField || organization?.config?.hasUserObjectiveField) && (
        <div className='user-profile-extra'>
          {organization?.config?.hasUserDiagnosticField && (
            <DiagnosticField diagnostic={user?.diagnostic} externalUserId={externalUserId} hasEditFeature={userHasEditFeature} />
          )}
          {organization?.config?.hasUserObjectiveField && (
            <ObjectiveField objective={user?.objective} externalUserId={externalUserId} hasEditFeature={userHasEditFeature} />
          )}
        </div>
      )}
      <ExternalUserSignedDocuments externalUser={user} />
    </Card>
  );
};
